import React from 'react';
import { GitMerge } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';

/**
 * Merge confirmation dialog for two clinic records.
 *
 * mergeTarget = { keep, discard }: the user picks direction in
 * DuplicateFinderModal ("Keep this, merge X"), then here edits the
 * merged name/address before confirming. onMerge POSTs to
 * /api/surgeons-db/clinics/merge; the backend unions the tags and
 * repoints surgeons on the discarded clinic (stated in the helper text —
 * see routes/surgeons.py). zIndex 60 keeps it above the z-50 finder
 * modal so they can stack.
 */

const tagClass = (tag) => {
  if (tag === 'ezyvet') return 'text-xs px-1.5 bg-green-100 text-green-700 rounded-full';
  if (tag === 'simons') return 'text-xs px-1.5 bg-blue-100 text-blue-700 rounded-full';
  return 'text-xs px-1.5 bg-gray-100 text-gray-600 rounded-full';
};
const tagLabel = (tag) => {
  if (tag === 'ezyvet') return 'EzyVet';
  if (tag === 'simons') return "Simon's";
  return tag;
};

const MergeClinicModal = ({ mergeTarget, mergeForm, setMergeForm, onCancel, onMerge }) => {
  return (
    // open={!!mergeTarget}: a null target means closed. z-60 keeps this
    // stacked above the z-50 duplicate-finder modal that spawned it.
    <Dialog open={!!mergeTarget} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-lg p-6 z-[60]" overlayClassName="bg-opacity-60 z-[60]">
        <div className="flex items-center space-x-2 mb-5">
          <GitMerge className="h-5 w-5 text-green-600" />
          <DialogTitle className="text-base">Merge Clinic Records</DialogTitle>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-5">
          <div className="border-2 border-green-400 rounded-xl p-3 bg-green-50">
            <div className="text-xs font-bold text-green-700 uppercase mb-1">Keep</div>
            <div className="text-sm font-semibold text-gray-900">{mergeTarget?.keep.name}</div>
            <div className="text-xs text-gray-500 mt-0.5">{mergeTarget?.keep.address}</div>
            <div className="flex gap-1 mt-1 flex-wrap">
              {(mergeTarget?.keep.tags || []).map(function(t) {
                return <span key={t} className={tagClass(t)}>{tagLabel(t)}</span>;
              })}
            </div>
          </div>
          <div className="border-2 border-red-300 rounded-xl p-3 bg-red-50">
            <div className="text-xs font-bold text-red-600 uppercase mb-1">Discard</div>
            <div className="text-sm font-semibold text-gray-900">{mergeTarget?.discard.name}</div>
            <div className="text-xs text-gray-500 mt-0.5">{mergeTarget?.discard.address}</div>
            <div className="flex gap-1 mt-1 flex-wrap">
              {(mergeTarget?.discard.tags || []).map(function(t) {
                return <span key={t} className={tagClass(t)}>{tagLabel(t)}</span>;
              })}
            </div>
          </div>
        </div>

        <div className="space-y-3 mb-5">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Merged name</label>
            <input
              value={mergeForm.merged_name || ''}
              onChange={function(e) { setMergeForm(function(f) { return Object.assign({}, f, {merged_name: e.target.value}); }); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Merged address</label>
            <input
              value={mergeForm.merged_address || ''}
              onChange={function(e) { setMergeForm(function(f) { return Object.assign({}, f, {merged_address: e.target.value}); }); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
            />
          </div>
          <p className="text-xs text-gray-400">Tags from both records will be combined. Surgeons linked to the discarded clinic will be repointed to the kept record.</p>
        </div>

        <div className="flex space-x-3">
          <button onClick={onCancel} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium">Cancel</button>
          <button onClick={onMerge} className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center justify-center space-x-1">
            <GitMerge className="h-4 w-4" /><span>Merge Records</span>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MergeClinicModal;
